import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Pencil,
  Save,
  Loader2,
  RefreshCw,
  Plus,
  X,
  Calculator,
  Wallet,
  Users,
  Receipt,
  PieChart,
  Megaphone,
  Settings,
  BarChart3,
  Target,
} from 'lucide-react';
import { servicesAPI } from '../../lib/api';
import { toast } from 'sonner';

const iconMap = {
  Calculator, Wallet, Users, Receipt, PieChart, Megaphone, Settings, BarChart3, Target
};

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    short_description: '',
    full_description: '',
    benefits: [],
    seo_title: '',
    seo_description: '',
  });
  const [newBenefit, setNewBenefit] = useState('');

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    setLoading(true);
    try {
      // Initialize services first
      await servicesAPI.init().catch(() => {});
      
      const response = await servicesAPI.getAll();
      setServices(response.data);
    } catch (error) {
      toast.error('Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title || '',
      short_description: service.short_description || '',
      full_description: service.full_description || '',
      benefits: service.benefits || [],
      seo_title: service.seo_title || '',
      seo_description: service.seo_description || '',
    });
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleAddBenefit = () => {
    if (newBenefit.trim()) {
      setFormData({
        ...formData,
        benefits: [...formData.benefits, newBenefit.trim()],
      });
      setNewBenefit('');
    }
  };

  const handleRemoveBenefit = (index) => {
    setFormData({
      ...formData,
      benefits: formData.benefits.filter((_, i) => i !== index),
    });
  };

  const handleSave = async () => {
    if (!editingService) return;
    
    setSaving(true);
    try {
      await servicesAPI.update(editingService.slug, formData);
      toast.success('Serviço atualizado com sucesso');
      setEditingService(null);
      loadServices();
    } catch (error) {
      toast.error('Erro ao atualizar serviço');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-services">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Serviços</h1>
          <p className="text-slate-600">Edite as descrições dos serviços exibidos no site</p>
        </div>
        <Button
          variant="outline"
          onClick={loadServices}
          disabled={loading}
          className="btn-secondary"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Array(6).fill(0).map((_, i) => (
            <div key={i} className="skeleton h-48 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service) => {
            const Icon = iconMap[service.icon] || Calculator;
            return (
              <Card key={service.slug} className="hover:shadow-md transition-shadow" data-testid={`service-${service.slug}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{service.title}</CardTitle>
                        <p className="text-xs text-slate-500">/{service.slug}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(service)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 text-sm line-clamp-3">
                    {service.short_description}
                  </p>
                  {service.benefits && service.benefits.length > 0 && (
                    <p className="text-xs text-slate-400 mt-3">
                      {service.benefits.length} benefício(s) listado(s)
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingService} onOpenChange={() => setEditingService(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Serviço: {editingService?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="title">Título</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="mt-1"
                data-testid="service-title-input"
              />
            </div>

            <div>
              <Label htmlFor="short_description">Descrição Curta</Label>
              <Textarea
                id="short_description"
                value={formData.short_description}
                onChange={(e) => handleChange('short_description', e.target.value)}
                rows={2}
                className="mt-1"
                placeholder="Usada nos cards da homepage"
              />
            </div>

            <div>
              <Label htmlFor="full_description">Descrição Completa</Label>
              <Textarea
                id="full_description"
                value={formData.full_description}
                onChange={(e) => handleChange('full_description', e.target.value)}
                rows={4}
                className="mt-1"
                placeholder="Usada na página individual do serviço"
              />
            </div>

            <div>
              <Label>Benefícios / O que está incluído</Label>
              <div className="mt-2 space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={newBenefit}
                    onChange={(e) => setNewBenefit(e.target.value)}
                    placeholder="Adicionar benefício"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddBenefit())}
                  />
                  <Button type="button" variant="outline" onClick={handleAddBenefit}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <ul className="space-y-2">
                  {formData.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-center gap-2 bg-slate-50 p-2 rounded">
                      <span className="flex-1 text-sm">{benefit}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveBenefit(index)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium text-slate-700 mb-3">SEO</p>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="seo_title">Título SEO</Label>
                  <Input
                    id="seo_title"
                    value={formData.seo_title}
                    onChange={(e) => handleChange('seo_title', e.target.value)}
                    placeholder={formData.title}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="seo_description">Descrição SEO</Label>
                  <Textarea
                    id="seo_description"
                    value={formData.seo_description}
                    onChange={(e) => handleChange('seo_description', e.target.value)}
                    rows={2}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingService(null)}>
              Cancelar
            </Button>
            <Button
              className="btn-primary"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-service-button"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminServices;
